"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    id: 1,
    name: "Sarah Chen",
    role: "Marketing Director",
    company: "HUX Hotel",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
    content: "Lateral cut our content creation time by 80%. The AI understands our brand perfectly and generates content that actually converts.",
    rating: 5,
  },
  {
    id: 2,
    name: "Marcus Johnson",
    role: "E-commerce Owner",
    company: "Just Toys Intl",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    content: "Finally, an AI tool that actually delivers. Our engagement rates have tripled since we started using Lateral for our social media.",
    rating: 5,
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    role: "Social Media Manager",
    company: "FoxFlorest",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    content: "The scheduling and multi-platform posting is seamless. Game changer for agencies managing multiple clients.",
    rating: 5,
  },
  {
    id: 4,
    name: "David Park",
    role: "Founder",
    company: "Herbdon",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    content: "We went from posting once a week to daily content across all platforms. The ROI has been incredible for our small team.",
    rating: 5,
  },
  {
    id: 5,
    name: "Lisa Thompson",
    role: "Brand Manager",
    company: "HUX Hotel",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face",
    content: "The AI-generated visuals are stunning. Our followers can't believe they're not from a professional photoshoot.",
    rating: 5,
  },
];

export function TestimonialsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  // Auto-advance carousel with proper cleanup
  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused]);

  // Get visible testimonials (show 3 at a time on desktop)
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push({ ...testimonials[index], position: i });
    }
    return visible;
  };

  // Calculate progress percentage
  const progressPercent = ((currentIndex + 1) / testimonials.length) * 100;

  // Animation variants - simple smooth fade
  const cardVariants = {
    enter: {
      opacity: 0,
    },
    center: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  };

  return (
    <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(5rem, 12vh, 9rem)', paddingBottom: 'clamp(5rem, 12vh, 9rem)' }}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-[280px_1fr] gap-12 items-start">

          {/* Left Column - Quote Icon + Heading + Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="lg:sticky lg:top-32 lg:-mt-12"
          >
            {/* Large Quote Icon - styled like reference */}
            <div className="mb-4">
              <svg
                className="w-20 h-16"
                style={{ color: "var(--section-index-color)" }}
                viewBox="0 0 80 60"
                fill="currentColor"
              >
                {/* Left quote mark */}
                <path d="M0 35C0 20 10 8 28 8v8c-10 0-16 6-16 14h12c2.2 0 4 1.8 4 4v18c0 2.2-1.8 4-4 4H4c-2.2 0-4-1.8-4-4V35z" />
                {/* Right quote mark */}
                <path d="M40 35c0-15 10-27 28-27v8c-10 0-16 6-16 14h12c2.2 0 4 1.8 4 4v18c0 2.2-1.8 4-4 4H44c-2.2 0-4-1.8-4-4V35z" />
              </svg>
            </div>

            {/* Green Section Title - like Gallery section */}
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-accent text-sm font-medium uppercase tracking-wider mb-4 block"
            >
              Testimonials
            </motion.span>

            {/* Heading with highlighted words */}
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 leading-tight">
              What our<br />
              <span className="text-gradient">customers</span> are<br />
              saying
            </h2>

            {/* Navigation with Progress Line - Green Gradient */}
            <div className="flex items-center gap-4">
              <button
                onClick={prevSlide}
                className="text-foreground-secondary hover:text-foreground transition-colors p-2 -m-2"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Progress Line - Green Gradient */}
              <div className="flex-1 h-[3px] bg-foreground/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: "linear-gradient(90deg, #10b981 0%, #a3e635 100%)",
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>

              <button
                onClick={nextSlide}
                className="text-foreground-secondary hover:text-foreground transition-colors p-2 -m-2"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Right Column - Testimonial Cards */}
          <div
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => {
              // Resume after a delay on touch end
              setTimeout(() => setIsPaused(false), 3000);
            }}
          >
            {/* Desktop Cards Grid (md+) - 3 cards in a row */}
            <div className="hidden md:grid md:grid-cols-3 gap-5">
              <AnimatePresence mode="popLayout">
                {getVisibleTestimonials().map((testimonial) => (
                  <motion.div
                    key={`desktop-${testimonial.id}-${currentIndex}`}
                    variants={cardVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      duration: 0.5,
                      ease: "easeInOut",
                    }}
                    className="relative"
                  >
                    <TestimonialCard testimonial={testimonial} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Tablet Cards (min-[480px] to md) - 3 cards alternating left/right */}
            <div className="hidden min-[480px]:flex md:hidden flex-col gap-6">
              <AnimatePresence mode="popLayout">
                {getVisibleTestimonials().map((testimonial, idx) => (
                  <motion.div
                    key={`tablet-${testimonial.id}-${currentIndex}`}
                    variants={cardVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      duration: 0.5,
                      ease: "easeInOut",
                      delay: idx * 0.08,
                    }}
                    className={`w-[75%] min-[560px]:w-[70%] sm:w-[65%] ${
                      idx % 2 === 0 ? 'self-start' : 'self-end'
                    }`}
                  >
                    <TestimonialCard testimonial={testimonial} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Mobile Cards (below 480px) - 3 cards stacked vertically */}
            <div className="flex min-[480px]:hidden flex-col gap-6">
              <AnimatePresence mode="popLayout">
                {getVisibleTestimonials().map((testimonial, idx) => (
                  <motion.div
                    key={`mobile-${testimonial.id}-${currentIndex}`}
                    variants={cardVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      duration: 0.5,
                      ease: "easeInOut",
                      delay: idx * 0.08,
                    }}
                    className="w-full"
                  >
                    <TestimonialCard testimonial={testimonial} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Testimonial Card Component
function TestimonialCard({
  testimonial,
  compact = false,
}: {
  testimonial: typeof testimonials[0] & { position: number };
  compact?: boolean;
}) {
  return (
    <div className={`relative ${compact ? 'mb-12' : 'mb-16'} ${compact ? 'min-h-[160px]' : 'min-h-[200px]'}`}>
      {/* SVG Speech Bubble Background - tail at bottom-left corner */}
      <svg
        className="absolute inset-0 w-full h-full overflow-visible"
        viewBox="0 0 300 200"
        preserveAspectRatio="none"
        fill="none"
      >
        {/* Speech bubble: rounded rect with tail extending down from bottom-left */}
        <path
          d="M16 0 H284 Q300 0 300 16 V168 Q300 184 284 184 H30 L15 200 L0 184 L0 16 Q0 0 16 0 Z"
          className="svg-bubble-fill svg-bubble-stroke"
          strokeWidth="1"
        />
      </svg>

      {/* Content inside bubble */}
      <div className={`relative ${compact ? 'p-4 pb-5' : 'p-5 pb-7'}`}>
        {/* Quote Content - fixed height container */}
        <div className={`${compact ? 'h-[90px]' : 'h-[110px]'} overflow-hidden mb-4`}>
          <p className={`text-foreground ${compact ? 'text-xs' : 'text-sm'} leading-relaxed`}>
            {testimonial.content}
          </p>
        </div>

        {/* Stars - always at same position */}
        <div className="flex items-center gap-0.5">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={i} className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} fill-accent text-accent`} />
          ))}
        </div>
      </div>

      {/* Author - outside bubble, positioned below the tail */}
      <div className={`absolute ${compact ? '-bottom-10' : '-bottom-14'} left-0 flex items-center gap-3`}>
        <img
          src={testimonial.avatar}
          alt={testimonial.name}
          className={`${compact ? 'w-10 h-10' : 'w-12 h-12'} rounded-full object-cover flex-shrink-0`}
        />
        <div>
          <p className={`font-medium text-foreground ${compact ? 'text-xs' : 'text-sm'}`}>{testimonial.name}</p>
          <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-foreground-secondary`}>
            {testimonial.role}, {testimonial.company}
          </p>
        </div>
      </div>
    </div>
  );
}
